import type { User } from "@/domain";
import type {
	IUserStorageService,
	IStorageService,
} from "@/application/ports/services";
import { STORAGE_KEYS } from "./constants";

/**
 * Pure User Storage Service
 *
 * This service handles only the persistence of user data to storage.
 * Following Clean Architecture principles:
 * - Pure storage operations without state management
 * - No reactive capabilities (handled by contexts)
 * - Simple, focused responsibility
 * - Infrastructure layer concern only
 */
export class UserStorageService implements IUserStorageService {
	private storageService: IStorageService;

	constructor(storageService: IStorageService) {
		this.storageService = storageService;
	}

	/**
	 * Get current user from storage
	 * Returns null if no user is stored
	 */
	getCurrentUser(): User | null {
		return this.storageService.get<User>(STORAGE_KEYS.AUTH_USER);
	}

	/**
	 * Set current user in storage
	 * Persists user data for app restart
	 */
	setCurrentUser(user: User | null): void {
		if (user === null) {
			this.storageService.remove(STORAGE_KEYS.AUTH_USER);
		} else {
			this.storageService.set(STORAGE_KEYS.AUTH_USER, user);
		}
	}

	/**
	 * Remove current user from storage
	 * Clears persisted user data
	 */
	removeCurrentUser(): void {
		this.storageService.remove(STORAGE_KEYS.AUTH_USER);
	}

	clearUser() {
		this.storageService.remove(STORAGE_KEYS.AUTH_USER);
	}
}
